/**
 * Unit-aware parsing for pasted values.
 *
 * Engineers paste from cut sheets, one-lines and emails: "112.5 kVA",
 * "0.5 MVA", "480Y/277", "12.47 kV", "1,200 ft", "150 m", "3.5%". A plain
 * <input type="number"> rejects all of these. parseUnitAware() extracts the
 * number and, when the pasted unit is in the same family as the FIELD's unit
 * (VA/W/V/A prefixes; ft/m/in lengths), converts it to the field's unit.
 * Anything else — an unknown unit, a different family — returns the bare
 * number: the engineer wanted the digits.
 */

const SI_PREFIX: Record<string, number> = { '': 1, k: 1e3, M: 1e6, m: 1e-3 };

/** Families for which prefix scaling applies (base unit, case-insensitive). */
const PREFIXED_BASES = ['VA', 'W', 'V', 'A', 'VAR', 'HZ'];

/** Length units expressed in feet. */
const LENGTH_IN_FT: Record<string, number> = { ft: 1, "'": 1, m: 3.280839895, in: 1 / 12, '"': 1 / 12 };

interface ParsedUnit {
  family: 'prefixed' | 'length' | 'percent' | 'other';
  /** For 'prefixed': multiplier to the unprefixed base; for 'length': feet per unit. */
  factor: number;
  base: string;
}

function classifyUnit(raw: string): ParsedUnit | null {
  const u = raw.trim();
  if (!u) return null;
  if (u === '%') return { family: 'percent', factor: 1, base: '%' };
  if (u in LENGTH_IN_FT) return { family: 'length', factor: LENGTH_IN_FT[u], base: 'ft' };
  const lower = u.toLowerCase();
  if (lower === 'ft' || lower === 'feet' || lower === 'foot') return { family: 'length', factor: 1, base: 'ft' };
  if (lower === 'in' || lower === 'inch' || lower === 'inches') return { family: 'length', factor: 1 / 12, base: 'ft' };
  if (lower === 'meter' || lower === 'meters' || lower === 'metre' || lower === 'metres') {
    return { family: 'length', factor: LENGTH_IN_FT.m, base: 'ft' };
  }
  // Prefixed electrical units: optional k/M/m + base. "m" alone is metres
  // (handled above); "mA"/"mV" are milli.
  const m = /^([kKmM]?)(VA|W|V|A|VAR|Hz)$/i.exec(u);
  if (m) {
    const base = m[2].toUpperCase();
    if (!PREFIXED_BASES.includes(base)) return null;
    let prefix = m[1];
    // "K" is not an SI prefix but engineers type "KVA"; "M" before A/V/W
    // could be mega or milli — mega is the electrical convention ("MVA"),
    // milli only when written lowercase.
    if (prefix === 'K') prefix = 'k';
    const factor = SI_PREFIX[prefix] ?? 1;
    return { family: 'prefixed', factor, base };
  }
  return { family: 'other', factor: 1, base: u };
}

/** True when the text is just a number (optionally signed / decimal / exponent). */
export function isPlainNumber(text: string): boolean {
  return /^\s*[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?\s*$/i.test(text);
}

/**
 * Parse pasted text into the field's unit. Returns null when no number can
 * be found. `fieldUnit` is the unit label the field displays ("kVA", "V",
 * "A", "ft", "%", "HP"); when omitted the bare number is returned.
 */
export function parseUnitAware(text: string, fieldUnit?: string): number | null {
  if (typeof text !== 'string') return null;
  // Thousands separators ("1,200", "12,470") and stray whitespace.
  const cleaned = text.replace(/(\d),(?=\d{3}(\D|$))/g, '$1').trim();
  if (!cleaned) return null;
  const m = /^[^\d\-+.]*([-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)\s*([A-Za-z%'"]*)/.exec(cleaned);
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value)) return null;
  let unitToken = m[2];
  // Voltage systems: "480Y/277", "480/277V", "208Y/120 V" → line-to-line
  // value; the unit rides after the second number.
  const rest = cleaned.slice(m[0].length);
  if (!unitToken && /^\s*Y?\s*\/\s*\d/.test(rest)) {
    const tail = /([A-Za-z%]+)\s*$/.exec(rest);
    unitToken = tail ? tail[1] : 'V';
  }
  if (!unitToken || !fieldUnit) return value;

  const from = classifyUnit(unitToken);
  const to = classifyUnit(fieldUnit);
  if (!from || !to) return value;
  if (from.family === 'prefixed' && to.family === 'prefixed' && from.base === to.base) {
    return roundTidy((value * from.factor) / to.factor);
  }
  if (from.family === 'length' && to.family === 'length') {
    return roundTidy((value * from.factor) / to.factor);
  }
  return value;
}

/** Trim a conversion to 3 decimals (150 m → 492.126 ft, not 492.12598425…). */
function roundTidy(v: number): number {
  return Math.round(v * 1000) / 1000;
}
